import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Cpu,
  FileText,
  Hash,
  Package,
  Truck,
} from "lucide-react";
import {
  getWorkflowTimeline,
  WorkflowDisputeEventContext,
  WorkflowDocEventContext,
  WorkflowEntityCreatedContext,
  WorkflowEntityType,
  WorkflowEvent,
  WorkflowPOAmendedContext,
  WorkflowStatusChangedContext,
} from "./workflow_repo";

type IconComponent = React.ComponentType<{ className?: string }>;

const STAGE_META: Record<string, { icon: IconComponent; description: string }> =
  {
    "Order Approved": {
      icon: ClipboardCheck,
      description: "Sales order approved",
    },
    "WH Yet To Be Assigned": {
      icon: Clock,
      description: "Awaiting warehouse assignment",
    },
    "WH Assigned": { icon: Clock, description: "Warehouse assigned" },
    "ST FG Approval Pending": {
      icon: ClipboardCheck,
      description: "Awaiting FG approval",
    },
    "Device Allocation Pending": {
      icon: Cpu,
      description: "Allocating devices",
    },
    "Insufficient Inventory": {
      icon: AlertTriangle,
      description: "Awaiting stock",
    },
    "Doc Pending": { icon: FileText, description: "Awaiting documents" },
    "Ready To Dispatch": {
      icon: CheckCircle2,
      description: "Ready to dispatch",
    },
    "Tracking ID Pending": { icon: Hash, description: "Awaiting tracking ID" },
    Dispatched: { icon: Truck, description: "Shipped, in transit" },
    Delivered: { icon: Package, description: "Delivered to destination" },
    Pending: { icon: Clock, description: "Awaiting receipt" },
    Partial: { icon: FileText, description: "Partially received" },
    Received: { icon: CheckCircle2, description: "Fully received" },
    Disputed: { icon: FileText, description: "Disputed" },
  };

const TERMINAL_FAILURE = new Set(["Rejected", "Cancelled", "Returned"]);

const INWARD_STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  partial: "Partial",
  received: "Received",
  disputed: "Disputed",
};

const normalizeStageLabel = (label: string | null | undefined): string => {
  if (!label) return "";
  return INWARD_STATUS_LABEL[label.toLowerCase()] ?? label;
};

const fallbackPlannedStages = (
  entityType: WorkflowEntityType,
  materialType?: "FG" | "RM",
): string[] => {
  if (entityType === "PO") return ["Pending", "Partial", "Received"];
  const isFG = materialType !== "RM";
  if (entityType === "SO") {
    return [
      "Order Approved",
      "WH Yet To Be Assigned",
      ...(isFG ? ["Device Allocation Pending"] : []),
      "Doc Pending",
      "Ready To Dispatch",
      "Tracking ID Pending",
      "Dispatched",
      "Delivered",
    ];
  }
  return [
    ...(isFG ? ["Device Allocation Pending"] : []),
    "Doc Pending",
    "Ready To Dispatch",
    "Tracking ID Pending",
    "Dispatched",
    "Delivered",
  ];
};

const formatDoc = (doc: string): string => {
  if (doc === "eway") return "E-way";
  if (doc === "dc") return "DC";
  if (doc === "invoice") return "Invoice";
  return doc;
};

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
};

const friendlyTransitionLabel = (prev: string, next: string): string => {
  // Re-approval after revert
  if (next === "Order Approved" && prev === "WH Yet To Be Assigned") {
    return "SO re-approved";
  }
  // Synthetic initial-approval advance
  if (prev === "Order Approved" && next === "WH Yet To Be Assigned") {
    return "Ready for warehouse assignment";
  }
  // Revert back to unassigned
  if (
    next === "WH Yet To Be Assigned" &&
    prev !== "Order Approved" &&
    prev !== "" &&
    prev != null
  ) {
    return "Order reverted to unassigned";
  }
  // ST: devices deallocated (Doc Pending → Device Allocation Pending)
  if (next === "Device Allocation Pending" && prev === "Doc Pending") {
    return "Devices deallocated";
  }
  // Terminal failures
  if (next === "Rejected") return "Order rejected";
  if (next === "Cancelled") return "Order cancelled";
  if (next === "Returned") return "Marked as returned";
  // Insufficient inventory flag
  if (next === "Insufficient Inventory") {
    return "Flagged: insufficient inventory";
  }
  // ST FG approval gate
  if (next === "ST FG Approval Pending") {
    if (prev === "Rejected") return "Edited & resubmitted for FG approval";
    return "Awaiting FG approval";
  }
  // Forward outward transitions
  if (next === "WH Assigned") return "Warehouse assigned";
  if (next === "Device Allocation Pending") {
    if (prev === "ST FG Approval Pending") {
      return "FG approved, devices to be allocated";
    }
    return "Warehouse assigned, devices to be allocated";
  }
  if (next === "Doc Pending") {
    if (prev === "Device Allocation Pending") {
      return "Devices allocated, documents awaited";
    }
    if (prev === "Insufficient Inventory") {
      return "Stock available, devices allocated";
    }
    if (prev === "Rejected") {
      return "Edited & resubmitted";
    }
    if (prev === "Ready To Dispatch") {
      return "Reverted to documents pending";
    }
    return "Awaiting documents";
  }
  if (next === "Ready To Dispatch") {
    return "Documents uploaded, ready to dispatch";
  }
  if (next === "Tracking ID Pending") return "Awaiting tracking ID";
  if (next === "Dispatched") {
    if (prev === "Tracking ID Pending") {
      return "Tracking ID added, order dispatched";
    }
    return "Order dispatched";
  }
  if (next === "Delivered") return "Order delivered";
  // Inward (PO / ST receipt) transitions
  if (next === "Partial" || next === "partial") return "Partially received";
  if (next === "Received" || next === "received") return "Fully received";
  if (next === "Disputed" || next === "disputed") return "Inward disputed";
  if (next === "Pending" || next === "pending") return "Awaiting receipt";
  // Fallback to raw
  return `${prev || "—"} → ${next}`;
};

const formatDuration = (ms: number): string => {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${days}d:${String(hours).padStart(2, "0")}h:${String(mins).padStart(2, "0")}m:${String(secs).padStart(2, "0")}s`;
};

interface StageView {
  stageLabel: string;
  meta: { icon: IconComponent; description: string };
  isTerminalAt: boolean;
  isActive: boolean;
  isDone: boolean;
  isInsufficient: boolean;
  circleClass: string;
  labelClass: string;
  showSegmentAfter: boolean;
  segIsFilled: boolean;
  duration: string | null;
  segColorClass: string;
}

const buildStageView = (
  stageLabel: string,
  idx: number,
  stages: string[],
  currentStageIdx: number,
  terminalStatus: string | null,
  effectiveStageTimestamps: Map<string, string>,
): StageView => {
  const meta = STAGE_META[stageLabel] ?? {
    icon: FileText,
    description: stageLabel,
  };
  const isTerminalAt = !!terminalStatus && idx === currentStageIdx;
  const isActive = !terminalStatus && idx === currentStageIdx;
  const isDone = currentStageIdx >= 0 && idx < currentStageIdx;
  const isInsufficient = stageLabel === "Insufficient Inventory";

  const circleClass = isTerminalAt
    ? "bg-red-600 border-red-600 text-white"
    : isInsufficient && (isActive || isDone)
      ? isActive
        ? "bg-red-500 border-red-500 text-white animate-pulse"
        : "bg-red-500 border-red-500 text-white"
      : isActive
        ? "bg-blue-600 border-blue-600 text-white animate-pulse"
        : isDone
          ? "bg-green-500 border-green-500 text-white"
          : isInsufficient
            ? "bg-white border-red-300 text-red-400"
            : "bg-white border-gray-300 text-gray-400";

  const labelClass = isTerminalAt
    ? "text-red-700 font-semibold"
    : isInsufficient && (isActive || isDone)
      ? "text-red-700 font-semibold"
      : isActive
        ? "text-blue-700 font-semibold"
        : isDone
          ? "text-green-700 font-semibold"
          : "text-gray-500";

  const showSegmentAfter = idx < stages.length - 1;
  const segIsFilled = currentStageIdx >= 0 && idx < currentStageIdx;
  const t1 = effectiveStageTimestamps.get(stageLabel);
  const t2 = effectiveStageTimestamps.get(stages[idx + 1]);
  const duration =
    segIsFilled && t1 && t2
      ? formatDuration(new Date(t2).getTime() - new Date(t1).getTime())
      : null;
  const segColorClass = segIsFilled ? "bg-green-500" : "bg-gray-300";

  return {
    stageLabel,
    meta,
    isTerminalAt,
    isActive,
    isDone,
    isInsufficient,
    circleClass,
    labelClass,
    showSegmentAfter,
    segIsFilled,
    duration,
    segColorClass,
  };
};

interface ActivityRowView {
  ev: WorkflowEvent;
  statusCtx: WorkflowStatusChangedContext | null;
  isRevertEv: boolean;
  isDisputeRaisedEv: boolean;
  isDisputeResolvedEv: boolean;
  isAmendedEv: boolean;
  label: React.ReactNode;
  rawTransition: string | null;
  subDetail: string | null;
  rowBgClass: string;
  textClass: string;
  rawTransitionClass: string;
  subDetailClass: string;
  timeClass: string;
}

const buildActivityRow = (
  ev: WorkflowEvent,
  stages: string[],
  entityType: WorkflowEntityType,
): ActivityRowView => {
  const statusCtx =
    ev.event_type === "status_changed"
      ? (ev.context as WorkflowStatusChangedContext)
      : null;

  const prevIdxForRevert = statusCtx
    ? stages.indexOf(normalizeStageLabel(statusCtx.prev_status))
    : -1;
  const nextIdxForRevert = statusCtx
    ? stages.indexOf(normalizeStageLabel(statusCtx.new_status))
    : -1;
  const isRevertEv =
    !!statusCtx &&
    prevIdxForRevert >= 0 &&
    nextIdxForRevert >= 0 &&
    nextIdxForRevert < prevIdxForRevert;

  const isDisputeRaisedEv = ev.event_type === "dispute_raised";
  const isDisputeResolvedEv = ev.event_type === "dispute_resolved";
  const isAmendedEv = ev.event_type === "po_amended";

  let label: React.ReactNode = null;
  let rawTransition: string | null = null;
  let subDetail: string | null = null;

  if (ev.event_type === "entity_created") {
    const ctx = ev.context as WorkflowEntityCreatedContext;
    if (ctx.revision_of) {
      label = `${entityType} created (revision of ${ctx.revision_of.inward_order_id})`;
      subDetail = `Previous version: v${ctx.revision_of.version}`;
    } else {
      label = entityType === "SO" ? "SO approved" : `${entityType} created`;
    }
  } else if (statusCtx) {
    label = friendlyTransitionLabel(
      statusCtx.prev_status,
      statusCtx.new_status,
    );
    const isInwardStreamEv = ev.status_stream === "inward";
    const prevDisplay =
      normalizeStageLabel(statusCtx.prev_status) ||
      (isInwardStreamEv ? "Pending" : "—");
    const nextDisplay =
      normalizeStageLabel(statusCtx.new_status) || statusCtx.new_status;
    rawTransition = `${prevDisplay} → ${nextDisplay}`;
  } else if (ev.event_type === "doc_uploaded") {
    const ctx = ev.context as WorkflowDocEventContext;
    label = `${formatDoc(ctx.doc_type)} uploaded${ctx.is_mandatory ? " (required)" : ""}`;
  } else if (ev.event_type === "doc_generated") {
    const ctx = ev.context as WorkflowDocEventContext;
    label = `${formatDoc(ctx.doc_type)} generated`;
  } else if (isDisputeRaisedEv) {
    const ctx = ev.context as WorkflowDisputeEventContext;
    const count = ctx.item_count ?? 1;
    const itemPart =
      count > 1
        ? ` on ${count} items`
        : ctx.item_name
          ? ` on ${ctx.item_name}${ctx.inventory_item_code ? ` (${ctx.inventory_item_code})` : ""}`
          : "";
    label = `Dispute raised${itemPart} — GRN blocked`;
    if (ctx.reason) subDetail = `Reason: ${ctx.reason}`;
  } else if (isDisputeResolvedEv) {
    label = "Dispute resolved";
    const ctx = ev.context as WorkflowDisputeEventContext;
    if (ctx.reason) subDetail = ctx.reason;
  } else if (isAmendedEv) {
    const ctx = ev.context as WorkflowPOAmendedContext;
    label = `PO amended — new version v${ctx.to_version} created`;
    subDetail = `New inward: ${ctx.to_inward_order_id}`;
  }

  const rowBgClass = isRevertEv
    ? "bg-red-50 border border-red-200 rounded px-2 py-1"
    : isDisputeRaisedEv
      ? "bg-orange-50 border border-orange-200 rounded px-2 py-1"
      : isDisputeResolvedEv
        ? "bg-green-50 border border-green-200 rounded px-2 py-1"
        : isAmendedEv
          ? "bg-blue-50 border border-blue-200 rounded px-2 py-1"
          : "px-1 py-0.5";

  const textClass = isRevertEv
    ? "text-red-700 font-medium"
    : isDisputeRaisedEv
      ? "text-orange-700 font-medium"
      : isDisputeResolvedEv
        ? "text-green-700 font-medium"
        : isAmendedEv
          ? "text-blue-700 font-medium"
          : "text-gray-900";

  const rawTransitionClass = isRevertEv
    ? "text-[10px] text-red-500/80 mt-0.5"
    : "text-[10px] text-gray-400 mt-0.5";

  const subDetailClass = isDisputeRaisedEv
    ? "text-[10px] text-orange-700/80 mt-0.5"
    : isDisputeResolvedEv
      ? "text-[10px] text-green-700/80 mt-0.5"
      : isAmendedEv
        ? "text-[10px] text-blue-700/80 mt-0.5"
        : "text-[10px] text-gray-500 mt-0.5";

  const timeClass = isRevertEv
    ? "text-red-600"
    : isDisputeRaisedEv
      ? "text-orange-700"
      : isDisputeResolvedEv
        ? "text-green-700"
        : isAmendedEv
          ? "text-blue-700"
          : "text-gray-500";

  return {
    ev,
    statusCtx,
    isRevertEv,
    isDisputeRaisedEv,
    isDisputeResolvedEv,
    isAmendedEv,
    label,
    rawTransition,
    subDetail,
    rowBgClass,
    textClass,
    rawTransitionClass,
    subDetailClass,
    timeClass,
  };
};

interface WorkflowTimelineRowProps {
  entityType: WorkflowEntityType;
  entityId: string;
  colSpan: number;
  includeInwardStages?: boolean;
  prefetchedEvents?: WorkflowEvent[];
  // When the parent always drives prefetchedEvents (e.g. via a shared batch
  // cache), set this so the row never falls back to its own per-entity fetch
  // — it just shows a loading state until prefetchedEvents arrives. Without
  // this, a row that mounts before the parent's batch fetch resolves fires
  // its own fetch anyway (prefetchedEvents is still undefined at that point),
  // and — since both the desktop table and mobile card are always mounted
  // (CSS-toggled, not conditionally rendered) — mounts twice, each firing its
  // own call, duplicating the request on top of the parent's batch call.
  deferToParent?: boolean;
}

const ST_INWARD_STAGES = ["Pending", "Partial", "Received"];

const WorkflowTimelineRow: React.FC<WorkflowTimelineRowProps> = ({
  entityType,
  entityId,
  colSpan,
  includeInwardStages = false,
  prefetchedEvents,
  deferToParent = false,
}) => {
  const [events, setEvents] = useState<WorkflowEvent[]>(prefetchedEvents ?? []);
  const [loading, setLoading] = useState(!prefetchedEvents);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (prefetchedEvents) {
      setEvents(prefetchedEvents);
      setLoading(false);
      setError(null);
      return;
    }
    if (deferToParent) {
      setLoading(true);
      return;
    }
    let mounted = true;
    setLoading(true);
    setError(null);
    getWorkflowTimeline(entityId).then((res) => {
      if (!mounted) return;
      if (res?.success && Array.isArray(res.data)) {
        setEvents(res.data);
      } else {
        setError(res?.error || "Failed to load timeline");
      }
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [entityId, prefetchedEvents, deferToParent]);

  const createdEvent = events.find((e) => e.event_type === "entity_created");
  const createdCtx = createdEvent?.context as
    | WorkflowEntityCreatedContext
    | undefined;

  const stages: string[] = useMemo(() => {
    const base = createdCtx?.planned_stages?.length
      ? createdCtx.planned_stages
      : fallbackPlannedStages(entityType, createdCtx?.material_type);

    let lastResetIdx = -1;
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      if (
        ev.event_type === "status_changed" &&
        (ev.context as WorkflowStatusChangedContext).new_status ===
          "WH Yet To Be Assigned"
      ) {
        lastResetIdx = i;
        break;
      }
    }
    const currentCycleEvents =
      lastResetIdx >= 0 ? events.slice(lastResetIdx + 1) : events;

    const hadInsufficient = currentCycleEvents.some(
      (ev) =>
        ev.event_type === "status_changed" &&
        (ev.context as WorkflowStatusChangedContext).new_status ===
          "Insufficient Inventory",
    );
    const withInsufficient = (() => {
      if (!hadInsufficient) return base;
      if (base.includes("Insufficient Inventory")) return base;
      const devAllocIdx = base.indexOf("Device Allocation Pending");
      if (devAllocIdx < 0) return base;
      const result = [...base];
      result.splice(devAllocIdx + 1, 0, "Insufficient Inventory");
      return result;
    })();

    if (
      includeInwardStages &&
      entityType === "ST" &&
      !withInsufficient.some((s) => ST_INWARD_STAGES.includes(s))
    ) {
      return [...withInsufficient, ...ST_INWARD_STAGES];
    }
    return withInsufficient;
  }, [createdCtx, entityType, events, includeInwardStages]);

  const activityRows = useMemo(
    () => events.map((ev) => buildActivityRow(ev, stages, entityType)),
    [events, stages, entityType],
  );

  const primaryStream = entityType === "PO" ? "inward" : "outward";
  const acceptInwardEvents = includeInwardStages && entityType === "ST";

  const stageTimestamps = useMemo(() => {
    const map = new Map<string, string>();
    for (const ev of events) {
      if (
        ev.event_type === "status_changed" &&
        ev.status_stream !== primaryStream &&
        !(acceptInwardEvents && ev.status_stream === "inward")
      ) {
        continue;
      }
      let stageLabel: string | null = null;
      if (ev.event_type === "entity_created") {
        stageLabel = (ev.context as WorkflowEntityCreatedContext)
          .initial_status;
      } else if (ev.event_type === "status_changed") {
        stageLabel = (ev.context as WorkflowStatusChangedContext).new_status;
      }
      const normalized = normalizeStageLabel(stageLabel);
      if (normalized && !map.has(normalized)) {
        map.set(normalized, ev.created_at);
      }
    }
    return map;
  }, [events, primaryStream, acceptInwardEvents]);

  const effectiveStageTimestamps = useMemo(() => {
    const filled = new Map<string, string>();
    let nextTs: string | undefined;
    for (let i = stages.length - 1; i >= 0; i--) {
      const stage = stages[i];
      const ts = stageTimestamps.get(stage);
      if (ts) {
        filled.set(stage, ts);
        nextTs = ts;
      } else if (nextTs) {
        filled.set(stage, nextTs);
      }
    }
    return filled;
  }, [stages, stageTimestamps]);

  const { currentStageIdx, terminalStatus } = useMemo(() => {
    let latestNonTerminal: string | null = null;
    let terminal: string | null = null;
    for (const ev of events) {
      if (
        ev.event_type === "status_changed" &&
        ev.status_stream !== primaryStream &&
        !(acceptInwardEvents && ev.status_stream === "inward")
      ) {
        continue;
      }
      let label: string | null = null;
      if (ev.event_type === "status_changed") {
        label = (ev.context as WorkflowStatusChangedContext).new_status;
      } else if (ev.event_type === "entity_created") {
        label = (ev.context as WorkflowEntityCreatedContext).initial_status;
      }
      const normalized = normalizeStageLabel(label);
      if (!normalized) continue;
      if (TERMINAL_FAILURE.has(normalized)) {
        terminal = normalized;
      } else {
        latestNonTerminal = normalized;
      }
    }
    const idx = latestNonTerminal ? stages.indexOf(latestNonTerminal) : -1;
    return { currentStageIdx: idx, terminalStatus: terminal };
  }, [events, stages, primaryStream, acceptInwardEvents]);

  const stageViews = useMemo(
    () =>
      stages.map((label, idx) =>
        buildStageView(
          label,
          idx,
          stages,
          currentStageIdx,
          terminalStatus,
          effectiveStageTimestamps,
        ),
      ),
    [stages, currentStageIdx, terminalStatus, effectiveStageTimestamps],
  );

  const requiredDocs = createdCtx?.required_docs ?? [];
  const optionalDocs = createdCtx?.optional_docs ?? [];
  const docPillText =
    requiredDocs.length || optionalDocs.length
      ? [
          requiredDocs.length
            ? `Required: ${requiredDocs.map(formatDoc).join(", ")}`
            : null,
          optionalDocs.length
            ? `Optional: ${optionalDocs.map(formatDoc).join(", ")}`
            : null,
        ]
          .filter(Boolean)
          .join(" • ")
      : null;

  const subtypeBadge =
    entityType === "ST" && (createdCtx?.st_subtype || createdCtx?.is_internal)
      ? [createdCtx?.is_internal ? "Internal" : null, createdCtx?.st_subtype]
          .filter(Boolean)
          .join(" / ")
      : null;

  return (
    <tr
      id={`warehouse-workflow-timeline-row-${entityId}`}
      className=" border border-b"
    >
      <td className="p-4 align-middle bg-gray-50" colSpan={colSpan}>
        {loading ? (
          <div className="text-xs text-gray-500">Loading timeline…</div>
        ) : error ? (
          <div className="text-xs text-red-600">{error}</div>
        ) : events.length === 0 ? (
          <div className="text-xs text-gray-500">
            No timeline events yet for this {entityType}.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-xs md:text-[10px] text-gray-500">
              <span className="flex items-center gap-2">
                Workflow Progress
                {subtypeBadge && (
                  <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-[10px] md:text-[9px]">
                    {subtypeBadge}
                  </span>
                )}
              </span>
              {docPillText && (
                <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded md:text-[10px]">
                  {docPillText}
                </span>
              )}
            </div>

            {terminalStatus && (
              <div className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 inline-block">
                Status: {terminalStatus}
                {currentStageIdx >= 0 && ` (at "${stages[currentStageIdx]}")`}
              </div>
            )}
            <div className="flex flex-col md:flex-row md:items-start pb-1">
              {stageViews.map((view) => {
                const {
                  stageLabel,
                  meta,
                  circleClass,
                  labelClass,
                  showSegmentAfter,
                  duration,
                  segColorClass,
                } = view;
                const StageIcon = meta.icon;
                return (
                  <React.Fragment key={stageLabel}>
                    <div className="flex items-center gap-3 md:flex-col md:items-center md:gap-0 md:w-20 md:flex-shrink-0">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${circleClass}`}
                      >
                        <StageIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0 md:flex-none md:mt-2 md:text-center">
                        <p className={`text-xs md:text-[10px] ${labelClass}`}>
                          {stageLabel}
                        </p>
                        <p className="text-xs md:text-[9px] text-gray-500 md:max-w-[80px]">
                          {meta.description}
                        </p>
                      </div>
                    </div>
                    {showSegmentAfter && (
                      <div className="flex items-center gap-3 md:relative md:flex-col md:items-stretch md:flex-1 md:min-w-[140px] md:gap-0 md:flex-shrink-0 md:mt-0 md:pt-[26px]">
                        <div className="w-10 flex justify-center md:hidden">
                          <div className={`w-0.5 h-6 ${segColorClass}`} />
                        </div>
                        <div
                          className={`hidden md:block ${segColorClass}`}
                          style={{
                            position: "absolute",
                            top: 19,
                            left: "-20px",
                            width: "calc(100% + 40px)",
                            height: 2,
                            boxSizing: "border-box",
                            pointerEvents: "none",
                          }}
                        />
                        <span
                          className="text-[10px] md:text-[9px] text-gray-600 whitespace-nowrap flex-1 md:flex-none md:w-full md:text-center md:leading-none"
                          style={{ minHeight: 10 }}
                        >
                          {duration ?? ""}
                        </span>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="border-t pt-3 space-y-1 max-h-48 overflow-y-auto sticky left-0 w-[80vw] max-w-full">
              {activityRows.map((row, i) => {
                const {
                  ev,
                  statusCtx,
                  isRevertEv,
                  label,
                  rawTransition,
                  subDetail,
                  rowBgClass,
                  textClass,
                  rawTransitionClass,
                  subDetailClass,
                  timeClass,
                } = row;
                return (
                  <div
                    key={ev._id ?? i}
                    className="flex flex-col gap-0.5 text-xs "
                  >
                    <div
                      className={`flex flex-col md:flex-row md:flex-nowrap items-start justify-between gap-0.5 md:gap-3 ${rowBgClass}`}
                    >
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className={`${textClass} break-words`}>
                          {isRevertEv && (
                            <span className="text-[10px] uppercase tracking-wide font-semibold bg-red-600 text-white rounded px-1 py-px mr-2">
                              Reverted
                            </span>
                          )}
                          {label}
                        </span>
                        {rawTransition && (
                          <span className={`${rawTransitionClass} break-words`}>
                            {rawTransition}
                          </span>
                        )}
                        {subDetail && (
                          <span className={`${subDetailClass} break-words`}>
                            {subDetail}
                          </span>
                        )}
                      </div>
                      <div className={`${timeClass} shrink-0 md:text-right`}>
                        <span className="break-words">
                          {ev.created_by_name}
                        </span>
                        {" • "}
                        <span className="whitespace-nowrap">
                          {formatDateTime(ev.created_at)}
                        </span>
                      </div>
                    </div>
                    {statusCtx?.remark && (
                      <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5 ml-2">
                        Remark: {statusCtx.remark}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};

export default WorkflowTimelineRow;
